import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { EmailTagInput } from '@/components/ui/email-tag-input';
import { Send } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { UnifiedInvoice, EmailFormData } from '@/types/invoicing';

interface SendEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: UnifiedInvoice | null;
  onEmailSent: () => void;
}

// Get default email message based on document type
const getDefaultEmailMessage = (invoice: UnifiedInvoice) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
  const previousMonthName = previousMonth.toLocaleDateString('en-US', { month: 'long' });

  switch (invoice.invoice_type) {
    case 'invoice':
      return `Dear Madame / Sir,\n\n` +
             `Please find attached our invoice for services provided through ${previousMonthName} ${currentYear}.\n\n` +
             `We are at your disposal if you have any questions.\n\n` +
             `With best regards,`;
    case 'credit_note':
      return `Dear Madame / Sir,\n\n` +
             `Please find attached our credit note.\n\n` +
             `We are at your disposal if you have any questions.\n\n` +
             `With best regards,`;
    case 'downpayment':
      return `Dear Madame / Sir,\n\n` +
             `Please find attached our down payment invoice.\n\n` +
             `We are at your disposal if you have any questions.\n\n` +
             `With best regards,`;
    default:
      return `Dear Madame / Sir,\n\n` +
             `Please find attached the requested document.\n\n` +
             `We are at your disposal if you have any questions.\n\n` +
             `With best regards,`;
  }
};

export function SendEmailDialog({ isOpen, onClose, invoice, onEmailSent }: SendEmailDialogProps) {
  const [emailFormData, setEmailFormData] = useState<EmailFormData>({
    message: '',
    additional_recipients: [],
    cc: [],
    bcc: [],
  });
  const [isSending, setIsSending] = useState(false);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);

  // Initialize form data when dialog opens
  useEffect(() => {
    if (isOpen && invoice) {
      setIsLoadingRecipients(true);
      
      // Set default message
      const defaultMessage = getDefaultEmailMessage(invoice);
      
      // Fetch recipients from server
      UnifiedInvoiceAPI.getRecipients(invoice.id)
        .then((recipientsData) => {
          // Separate recipients by type
          const toRecipients = recipientsData.recipients
            .filter(r => r.type === 'TO')
            .map(r => r.email);
          const ccRecipients = recipientsData.recipients
            .filter(r => r.type === 'CC')
            .map(r => r.email);
          const bccRecipients = recipientsData.recipients
            .filter(r => r.type === 'BCC')
            .map(r => r.email);

          setEmailFormData({
            message: defaultMessage,
            additional_recipients: toRecipients,
            cc: ccRecipients,
            bcc: bccRecipients,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch recipients:', error);
          // Fallback to default form data
          setEmailFormData({
            message: defaultMessage,
            additional_recipients: [],
            cc: [],
            bcc: [],
          });
          toast({
            title: 'Warning',
            description: 'Could not load default recipients',
            variant: 'destructive',
          });
        })
        .finally(() => {
          setIsLoadingRecipients(false);
        });
    }
  }, [isOpen, invoice]);

  const handleSendEmail = async () => {
    if (!invoice) return;

    setIsSending(true);
    try {
      await UnifiedInvoiceAPI.sendEmail(invoice.id, emailFormData);

      toast({
        title: 'Success',
        description: 'Email sent successfully',
      });

      onEmailSent();
      handleClose();
    } catch (error) {
      console.error('Failed to send email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send email',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setEmailFormData({
      message: '',
      additional_recipients: [],
      cc: [],
      bcc: [],
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Email</DialogTitle>
          <DialogDescription>
            Send {invoice?.invoice_number} via email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Message</Label>
            <Textarea
              value={emailFormData.message}
              onChange={(e) => setEmailFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Enter your message..."
              rows={6}
            />
          </div>

          <div>
            <Label>Additional Recipients (TO)</Label>
            <EmailTagInput
              value={emailFormData.additional_recipients}
              onChange={(emails) => setEmailFormData(prev => ({ 
                ...prev, 
                additional_recipients: emails
              }))}
              placeholder="Enter email address and press Enter..."
            />
          </div>

          <div>
            <Label>CC Recipients</Label>
            <EmailTagInput
              value={emailFormData.cc}
              onChange={(emails) => setEmailFormData(prev => ({ 
                ...prev, 
                cc: emails
              }))}
              placeholder="Enter CC email address and press Enter..."
            />
          </div>

          <div>
            <Label>BCC Recipients</Label>
            <EmailTagInput
              value={emailFormData.bcc}
              onChange={(emails) => setEmailFormData(prev => ({ 
                ...prev, 
                bcc: emails
              }))}
              placeholder="Enter BCC email address and press Enter..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSending || isLoadingRecipients}>
            Cancel
          </Button>
          <Button onClick={handleSendEmail} disabled={isSending || isLoadingRecipients}>
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-2"></div>
                Sending...
              </>
            ) : isLoadingRecipients ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-2"></div>
                Loading Recipients...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
